import { Badge, Group, Tabs, Text, useMantineTheme } from '@mantine/core';
import { useDebtor } from 'app/Private/Clients/Detail/client-atom';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { Monitoring } from 'modules/Debtor/monitoring';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { isEmpty, isNonNull, isNonNullish, isNullish } from 'remeda';
import { useLoadTasks } from 'shared/hooks/use-load-tasks';
import { ActivitiesTab } from './ActivitiesTab';
import { BalanceTab } from './BalanceTab';
import { InfoTabAtRisk } from './InfoTab';
import { InvoiceKanbanTab } from './InvoiceKanbanTab';
import { PreferenceTab } from './PreferenceTab';
import { TaskTab } from './TaskTab';
import { ThirdpartyCaseTab } from './ThirdpartyCaseTab';
import { UnhandledInvoicesTab } from './UnhandledInvoicesTab';

export const ClientTabs = () => {
  const location = useLocation();
  const history = useHistory();

  const debtor = useDebtor();
  const tabItems = useBuildTabItems();
  const theme = useMantineTheme();

  const { tasks } = useLoadTasks(
    { debtor_id: String(debtor.id) },
    { enabled: isNonNullish(debtor.id) },
  );

  const [activeSlug, setActiveSlug] = useState(getSlugFromHash(location.hash) ?? tabItems[0].slug);

  useEffect(() => {
    const newTabSlug = getSlugFromHash(location.hash);
    if (isNullish(newTabSlug)) return;
    setActiveSlug(newTabSlug);
  }, [location.hash]);

  const handleTabChange = (index: number) => {
    const newSlug = tabItems[index].slug;
    setActiveSlug(newSlug);
    history.replace(`#${newSlug}`);
  };

  const uncompletedTasksCount =
    tasks?.data?.filter(task => task.status_key === 'to_do').length ?? 0;

  return (
    <Tabs value={activeSlug} keepMounted={false} style={{ height: '100%' }}>
      <Tabs.List>
        {tabItems.map(({ title, slug }, index) => (
          <Tabs.Tab value={slug} key={slug} onClick={() => handleTabChange(index)}>
            <Group gap={8}>
              <Text>{title}</Text>
              {slug === 'tasks' && uncompletedTasksCount > 0 && (
                <Badge size="xs" color="coral.5" radius={6}>
                  {uncompletedTasksCount}
                </Badge>
              )}
              {slug === 'at-risk' && (
                <Badge
                  styles={{ root: { border: `1px solid ${theme.colors.coral[4]}` } }}
                  color="coral.2"
                  size="sm"
                  radius={6}
                >
                  <Text fw={500} fz={12} c="coral.6">
                    Beta
                  </Text>
                </Badge>
              )}
            </Group>
          </Tabs.Tab>
        ))}
      </Tabs.List>

      {tabItems.map(({ slug, title, children }) => (
        <Tabs.Panel value={slug} key={`tab-${title}`} style={{ height: '100%' }}>
          {children}
        </Tabs.Panel>
      ))}
    </Tabs>
  );
};

function getSlugFromHash(hash: string) {
  if (isEmpty(hash)) return null;
  return hash.replace('#', '');
}

function useBuildTabItems() {
  const { t } = useTranslation();

  const company = useCompanyConfiguration();
  const debtor = useDebtor();

  const tabs = [
    {
      title: t(i18nKeys.CLIENT.TABS.GENERAL_INFO),
      slug: 'general-info',
      children: <InfoTabAtRisk />,
    },
    {
      title: t(i18nKeys.CLIENT.TABS.BALANCE),
      slug: 'balance-detail',
      children: <BalanceTab />,
    },
    {
      title: t(i18nKeys.CLIENT.TABS.KANBAN),
      slug: 'invoices-kanban',
      children: <InvoiceKanbanTab />,
    },
    {
      title: t(i18nKeys.INVOICE.ACTIVITIES),
      slug: 'activities',
      children: <ActivitiesTab />,
    },
    {
      title: t(i18nKeys.CLIENT.TABS.PREFERENCES),
      slug: 'preferences',
      children: <PreferenceTab />,
    },
  ];

  const isTasksModuleEnabledInPackage = company.package.can_use_tasks ?? false;
  if (isTasksModuleEnabledInPackage)
    tabs.splice(3, 0, {
      title: t(i18nKeys.CLIENT.TABS.TASKS),
      slug: 'tasks',
      children: <TaskTab />,
    });

  tabs.splice(3, 0, {
    title: t(i18nKeys.MODULES.AT_RISK.TAB_TITLE),
    slug: 'at-risk',
    children: <UnhandledInvoicesTab />,
  });

  const thirdPartyCaseCount = debtor.tp_cases_count ?? 0;
  if (thirdPartyCaseCount > 0)
    tabs.splice(5, 0, {
      title: t(i18nKeys.THIRD_PARTY_CASES.CURRENT_CASES),
      slug: 'thirdparty-case-table',
      children: <ThirdpartyCaseTab />,
    });

  const vatNumber = debtor.vat_number;
  tabs.splice(-1, 0, {
    title: t(i18nKeys.DEBTOR.MONITORING.TAB_TITLE),
    slug: 'monitoring',
    children: <Monitoring hasVatNumber={isNonNull(vatNumber) && !isEmpty(vatNumber)} />,
  });

  return tabs;
}
